// TODO rework when im fixing bugs
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import {
  ExerciseWithLearningDataFilter,
  LearningDataAPI,
} from '@chancenwerk/campus-learning-content/api/LearningDataAPI.js';
import { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import { CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { ContentResultEventlog } from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { combineLatest, debounceTime, filter, map, of, switchMap } from 'rxjs';
import { CampusRoutes } from '../routes.js';

/**
 * @customElement campus-page-student-knowledge-answer-details
 */
@customElement('campus-page-student-knowledge-answer-details')
export class CampusPageStudentKnowledgeAnswerDetails extends ChemistryLitElement {
  @property() selectedUserID = new RXController(this, '');

  override styles() {
    return [{ ':host': { height: '100%' } }];
  }

  @state() size = new WindowSizeController(this);

  @state() private readonly _currentStudent = new RXController(
    this,
    this.selectedUserID.observable.pipe(
      filter(Boolean),
      switchMap(id => ChancenwerkUser.subscribeByID<ChancenwerkUser>(id)({})),
    ),
  );
  @state() private readonly _lessonSearch = new RXController(this, '');
  @state() private readonly _lessonsFilter = new RXController(
    this,
    combineLatest([this.selectedUserID.observable, this._lessonSearch.observable.pipe(debounceTime(200))]).pipe(
      map(
        ([userID, search]) =>
          new ExerciseWithLearningDataFilter({
            ...(userID ? { studentID: userID } : {}),
            ...(search ? { search } : {}),
          }),
      ),
    ),
  );

  @state() private readonly _selectedLesson = new RXController<Maybe<CampusLesson>>(this, undefined);
  @state() private readonly _exercisesWithAnswers = new RXController(
    this,
    combineLatest([this._selectedLesson.observable, this.selectedUserID.observable]),
    obs$ =>
      obs$.pipe(
        switchMap(([lesson, userID]) => {
          if (!lesson || !userID) return of([]);
          return LearningDataAPI.getExercisesWithAnswers(
            lesson._id,
            userID,
          )({ ...CampusExercise.allFields, answers: ContentResultEventlog.allFields });
        }),
      ),
  );

  private readonly _columnsDefinition: ColumnDefinition<typeof CampusLesson> = [
    {
      columnName: 'Name der Lektion',
      fieldName: 'name',
      columnWidth: '3fr',
    },
    {
      customID: 'action',
      columnWidth: '1fr',
      renderItem: lesson =>
        html`
          <cr-button mode="outline" icon="visibility" @click=${() => this._selectedLesson.next(lesson)}> </cr-button>
        `,
    },
  ] as Array<ColumnDefinition<CampusLesson>>;

  override render() {
    return permissionGuard(ChancenPermission.campus.READ_DIAGNOSTIC, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    if (this._currentStudent.isLoading) return this._loader();
    if (!this._currentStudent.value) return this._emptyStudentState();
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex padding="md" space="lg">
          <cr-flex horizontal crossaxis-center>
            <cr-icon ${css({ fontSize: '1.8em', cursor: 'pointer' })} @click=${() => goTo(CampusRoutes.diagnostics)}>
              arrow_back_ios
            </cr-icon>

            <cr-flex space-between horizontal crossaxis-center ${css({ width: '100%' })}>
              <cr-h2> Antworten in den Lektionen </cr-h2>
            </cr-flex>
          </cr-flex>
          <cr-text> Hier kannst du die Antworten der Lektionen einsehen. </cr-text>
        </cr-flex>
        ${this._selectedLesson.value ? this._lessonDetails() : this._lessonOverview()}
      </cr-flex>
    `;
  }

  private _lessonDetails() {
    if (!this._selectedLesson.value) return html` 404 `;
    return html`
      <cr-flex space="lg">
        <cr-flex horizontal crossaxis-center space="md">
          <cr-button mode="icon" icon="close" @click=${() => this._selectedLesson.next(null)}> </cr-button>
          <cr-text> Aktuell ausgewählte Lektion: ${this._selectedLesson.value.name} </cr-text>
        </cr-flex>
        <cr-flex space="md">
          ${repeat(this._exercisesWithAnswers.value ?? [], exercise => {
            if (exercise.questionText?.trim().length === 0) return nothing;

            const lastAnswer = [...(exercise.answers ?? [])].splice(-1)[0];
            const variables: string[] = [];
            let lastIndex = 0;
            if (exercise.questionAnswerVariableFields) {
              exercise.questionAnswerVariableFields.replace(
                CampusExercise.latexExpressionRegex,
                (match, group, index, fullString) => {
                  const subStr = fullString.slice(lastIndex, index);
                  lastIndex = index;
                  variables.push(subStr.slice(subStr.search(/[\w]+=/)));
                  return match;
                },
              );
            }

            const results = lastAnswer?.answer.split('|') ?? [];
            const variableResults = results[0]?.split(';') ?? [];

            if (results.length === 0) return nothing;

            return html`
              <cr-card ${css({ position: 'relative' })}>
                <cr-flex space="lg">
                  <cr-flex space="md">
                    <cr-text bold> Aufgabe: </cr-text>
                    <cr-text> ${unsafeHTML(exercise.questionText)} </cr-text>
                  </cr-flex>
                  <cr-flex space="md">
                    <cr-text bold> Eingegebene Antwort: </cr-text>
                    ${variables.length
                      ? html`
                          <cr-flex space="sm">
                            <cr-text tone="subtle"> Mathematische Variablen </cr-text> ${repeat(
                              variables,
                              (variable, index) => html` <cr-text> ${variable}${variableResults[index]} </cr-text> `,
                            )}
                          </cr-flex>
                        `
                      : nothing}
                    <cr-flex padding="sm" ${css({ background: this.colors.neutral.shade(50) })}>
                      <cr-text> ${unsafeHTML(results.slice(1).join('|'))} </cr-text>
                    </cr-flex>
                  </cr-flex>
                </cr-flex>
                ${exercise.questionAnswerVariableFields
                  ? html`
                      <cr-flex
                        ${css({
                          position: 'absolute',
                          top: '0',
                          right: '0',
                          borderBottomLeftRadius: this.sizes.borderRadius,
                          background: lastAnswer?.result ? this.colors.tone.success : this.colors.tone.error,
                          color: lastAnswer?.result ? 'white' : this.colors.on.tone.error,
                          fontSize: this.spacing.lg,
                        })}
                        padding="sm"
                      >
                        <cr-icon> ${lastAnswer?.result ? 'check_circle' : 'close'} </cr-icon>
                      </cr-flex>
                    `
                  : nothing}
              </cr-card>
            `;
          })}
        </cr-flex>
      </cr-flex>
    `;
  }

  private _lessonOverview() {
    if (!this._lessonsFilter.value) return html` <cr-loader></cr-loader> `;
    return html`
      <cr-flex space="lg">
        <cr-text> Wähle eine Lektion aus, um die Antworten dieses Schülers dafür zu sehen: </cr-text>
        <cr-input
          placeholder="Lektionen suchen..."
          @value-changed=${(e: ValueEvent<string>) => this._lessonSearch.next(e.detail.value)}
        ></cr-input>
        <x-data-view
          .options=${{
            class: CampusLesson,
            operation: LearningDataAPI.subscribeLessonWithAnswers,
            view: new View({ filter: this._lessonsFilter.value }),
            onlyShowLastTranslation: true,
            customColumns: this._columnsDefinition,
          } as DataViewOptions<typeof CampusLesson>}
        >
        </x-data-view>
      </cr-flex>
    `;
  }

  private _loader() {
    return html`
      <cr-flex center crossaxis-center w100 h100>
        <cr-campus-loader></cr-campus-loader>
      </cr-flex>
    `;
  }

  private _emptyStudentState() {
    return html`
      <cr-flex center crossaxis-center w100 h100 space="sm">
        <cr-h4>Oops, diese Person wurde nicht gefunden.</cr-h4>
        <cr-button @click=${() => goTo(CampusRoutes.diagnostics)}>ZURÜCK</cr-button>
      </cr-flex>
    `;
  }
}
